import Axios from 'axios'
import pluralize from 'pluralize'
import { AppSharedPropsStore } from 'src/app'
import { asyncToast } from 'src/app/toast'
import { dayjs } from 'src/lib/dayjs'
import { copyText } from 'src/utils/clipboard'

import { IPlaylist, IPlaylistHighlight, IPlaylistItem } from './types'

export async function activateShareUrl(playlist: IPlaylist) {
  await Axios.put<{ share_url: string }>(playlist.actions.activateShareUrl).then(async ({ data }) => {
    playlist.shareUrl = data.share_url
  })
  return playlist.shareUrl
}

export async function activateHighlightShareUrl(playlistItem: IPlaylistHighlight) {
  await Axios.put<{ share_url: string }>(playlistItem.actions.activateShareUrl).then(async ({ data }) => {
    playlistItem.shareUrl = data.share_url
  })
  return playlistItem.shareUrl
}

export async function copyShareUrl(playlist: IPlaylist) {
  if (playlist.shareUrl) {
    const copyPromise = copyText(playlist.shareUrl)
    asyncToast(copyPromise, 'Copying…', 'Copied', 'Error copying')
    return copyPromise
  } else {
    const activatePromise = activateShareUrl(playlist).then((shareUrl) => ({ plain_text: shareUrl }))
    const copyPromise = copyText(activatePromise, true)
    asyncToast(copyPromise, 'Copying…', 'Copied', 'Error generating share url')
    return copyPromise
  }
}

export async function copyHighlightShareUrl(playlistItem: IPlaylistHighlight) {
  if (playlistItem.shareUrl) {
    const copyPromise = copyText(playlistItem.shareUrl)
    asyncToast(copyPromise, 'Copying…', 'Copied', 'Error copying')
    return copyPromise
  } else {
    const activatePromise = activateHighlightShareUrl(playlistItem).then((shareUrl) => ({ plain_text: shareUrl }))
    const copyPromise = copyText(activatePromise, true)
    asyncToast(copyPromise, 'Copying…', 'Copied', 'Error generating share url')
    return copyPromise
  }
}

export function getPlaylistName(playlist?: IPlaylist): string {
  if (playlist?.hashtag) {
    return `#${playlist.hashtag}`
  } else if (playlist?.name) {
    return playlist.name
  } else {
    return 'the playlist'
  }
}

export function getPlaylistShareDisabledReason(playlist: IPlaylist) {
  const shareUnavailable = !(playlist?.actions?.activateShareUrl || playlist?.shareUrl)
  const shareDisabledByTeamAdmin =
    AppSharedPropsStore.state.currentUser?.team?.preferences?.share_links_enabled === false

  return shareDisabledByTeamAdmin
    ? 'Disabled by Team Administrator'
    : shareUnavailable
    ? 'Sharing is not available for this playlist'
    : null
}

export function getDurationMinutes(playlist: IPlaylist) {
  return Math.round(dayjs.duration(playlist.durationSeconds >= 60 ? playlist.durationSeconds : 60, 's').asMinutes())
}

export function getItemsCountString(playlist: IPlaylist): string {
  const callCount = playlist.callCount
  const hasCalls = callCount > 0
  const highlightCount = playlist.highlightCount
  const hasHighlights = highlightCount > 0
  const itemsCount = callCount + highlightCount
  let itemsLabel = 'item'
  if (hasCalls && !hasHighlights) {
    itemsLabel = 'call'
  } else if (!hasCalls && hasHighlights) {
    itemsLabel = 'highlight'
  }
  return pluralize(itemsLabel, itemsCount, true)
}

export function getRecordingStartedAtString(playlistItem: IPlaylistItem): string {
  const recordingStartedAt = dayjs(playlistItem.callMetadata.recordingStartedAt)
  return recordingStartedAt.year() === dayjs().year()
    ? recordingStartedAt.format('MMM D')
    : recordingStartedAt.format('MMM D, YYYY')
}

export function getUpdatedAtString(playlist: IPlaylist): string {
  const currentYear = dayjs().year()
  const updatedAt = dayjs(playlist.updated_at)
  const formatString = updatedAt.year() === currentYear ? 'MMM D' : 'MMM D, YYYY'
  return updatedAt.format(formatString)
}
